// Renders the integration guide as a markdown file an admin can send to the
// partner's developers, who have no CentraOne login. Built from the same
// sections as the in-app guide, so the two cannot drift apart.

import {
  GO_LIVE_ITEMS,
  SAMPLE_LANG_LABELS,
  buildGuide,
  type GuideBlock,
  type GuideContext,
  type SampleLang,
} from './guideContent';

const FENCE_LANG: Record<SampleLang, string> = {
  curl: 'bash',
  node: 'js',
  python: 'python',
  csharp: 'csharp',
  json: 'json',
  http: 'http',
};

function cell(text: string): string {
  return text.replace(/\|/g, '\\|').replace(/\n/g, ' ');
}

function blockToMarkdown(block: GuideBlock): string {
  switch (block.type) {
    case 'p':
      return block.text;
    case 'heading':
      return `### ${block.text}`;
    case 'list':
      return block.items
        .map((item, index) => `${block.ordered ? `${index + 1}.` : '-'} ${item}`)
        .join('\n');
    case 'note':
      return `> **${block.tone === 'warning' ? 'Important' : 'Note'}:** ${block.text}`;
    case 'table':
      return [
        `| ${block.head.map(cell).join(' | ')} |`,
        `| ${block.head.map(() => '---').join(' | ')} |`,
        ...block.rows.map((row) => `| ${row.map(cell).join(' | ')} |`),
      ].join('\n');
    case 'code':
      return block.samples
        .map((sample) => {
          const label = block.samples.length > 1 ? `**${SAMPLE_LANG_LABELS[sample.lang]}**\n\n` : '';
          return `${label}\`\`\`${FENCE_LANG[sample.lang]}\n${sample.code}\n\`\`\``;
        })
        .join('\n\n');
    case 'checklist':
      return GO_LIVE_ITEMS.map((item) => `- [ ] ${item.text}`).join('\n');
  }
}

export function guideToMarkdown(ctx: GuideContext, generatedAt: Date = new Date()): string {
  const sections = buildGuide(ctx);
  const preparedFor = ctx.productName
    ? `Prepared for **${ctx.productName}**${ctx.keyName ? ` (API key "${ctx.keyName}")` : ''}.`
    : ctx.keyName
      ? `Prepared for API key "${ctx.keyName}".`
      : null;

  const parts = [
    '# Integrating with CentraOne',
    [
      `*Generated ${generatedAt.toISOString().slice(0, 10)} for ${ctx.baseUrl}.*`,
      preparedFor,
      'The API key itself is not in this file; it is sent separately over a secure channel.',
    ]
      .filter(Boolean)
      .join(' '),
    ...sections.map((section, index) =>
      [`## ${index + 1}. ${section.title}`, ...section.blocks.map(blockToMarkdown)].join('\n\n'),
    ),
  ];
  return `${parts.join('\n\n')}\n`;
}

/** File name for the download, e.g. `centraone-integration-guide-control-tower.md`. */
export function guideFileName(ctx: Pick<GuideContext, 'productSlug'>): string {
  return `centraone-integration-guide${ctx.productSlug ? `-${ctx.productSlug}` : ''}.md`;
}
